import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import * as XLSX from 'xlsx'

import { useTranslation } from 'react-i18next'
import { PurchasesSummaryService } from '../../../services/purchase-fuel/summary/purchasesSummary.service'

import useHandlerBasicActions from '../../../utils/hooks/useHandlerBasicActions'
import PurchaseSummaryDataTable from '../../../components/purchase-fuel/summary/PurchaseSummaryDataTable'

import { OwnFuelStationsService } from '../../../services/own-fuel-stations/ownFuelStations.service'
import { SuppliersService } from '../../../services/purchase-fuel/suppliers/suppliers.service'
import swalAlert from '../../../utils/helpers/swalAlert'
import AppSection from '../../../component/layout/AppSection.tsx'
import AppTabMenu from '../../../component/layout/AppTabMenu.tsx'
import { purchaseFuels } from '../../../utils/constants/navlinks-Menu'
import AppToolbar from '../../../component/layout/AppToolbar.tsx'
import EntityDropdown from '../../../component/form/EntityDropdown.tsx'
import AppButton from '../../../component/common/AppButton.tsx'
import AppRangePicker from '../../../component/form/AppRangePicker.tsx'
import { Group } from '@mantine/core'

const Summary = () => {
  const { formatMessage: msg } = useIntl()
  const { t } = useTranslation()

  const [selectedDateRange, setSelectedDateRange] = useState([
    undefined,
    undefined,
  ])

  const [currentOwnStation, setCurrentOwnstation] = useState()
  const [currentSupplier, setCurrentSupplier] = useState()
  const [currentIsTotal, setCurrentIsTotal] = useState('')
  const [exporting, setExporting] = useState(false)
  const [comingBack, setComingBack] = useState(false)

  const initialValues = {
    dateFrom: null,
    dateTo: null,
    ownStation: 'all',
    supplier: 'all',
    isTotal: false,
  }

  const retrieveOwnstations = async () => {
    const resp = await OwnFuelStationsService.getAll({ sort: 'centerName' })
    const rows = resp?.resp?.map((item) => {
      return { value: item?._id, label: item?.centerName }
    })
    rows.unshift({
      value: 'all',
      label: msg({ id: 'all' }).toUpperCase().concat(' ***'),
    })
    rows.unshift({ value: null, label: '...' })
    // setOwnStations(rows)
  }

  const retrieveSuppliers = async () => {
    const resp = await SuppliersService.getAll({ sort: 'name' })
    const rows = resp?.resp?.map((item) => {
      return { value: item?._id, label: item?.name }
    })
    rows.unshift({
      value: 'all',
      label: msg({ id: 'all' }).toUpperCase().concat(' ***'),
    })
    rows.unshift({ value: null, label: '...' })
    // setSuppliers(rows)
  }

  const dataFacade = useHandlerBasicActions({
    getAll: ({ filter, sort, limit, skip }) =>
      PurchasesSummaryService.getAll({
        dateFrom: selectedDateRange[0]
          ? new Date(selectedDateRange[0]).toISOString()
          : undefined,
        dateTo: selectedDateRange[1]
          ? new Date(selectedDateRange[1]).toISOString()
          : undefined,
        ownStation: currentOwnStation || undefined,
        supplier: currentSupplier || undefined,
        isTotal: currentIsTotal === 'true' || undefined,
        filter,
        sort,
        limit: exporting ? dataFacade?.totalRecords : limit,
        skip,
      }),
    rowsPerPageInitial: 100,
    sortFieldInitial: 'centerName',
    sortInit: 1,
  })

  const handleDownload = async () => {
    setExporting(true)
  }

  useEffect(() => {
    retrieveOwnstations()
    retrieveSuppliers()
  }, [])

  useEffect(() => {
    const someOne = [
      selectedDateRange[0],
      selectedDateRange[1],
      currentOwnStation,
      currentSupplier,
      currentIsTotal,
    ].some((s) => !!s)
    if (someOne) dataFacade.reload()
  }, [selectedDateRange, currentOwnStation, currentSupplier, currentIsTotal])

  useEffect(() => {
    if (exporting && !dataFacade?.loading) {
      const workbook = XLSX.utils.table_to_book(
        document.getElementsByClassName('p-datatable-table')[0],
      )
      const filename = 'CARBURANTES_COMPRA_RESUMEN.xlsx'
      XLSX.writeFile(workbook, filename)
      swalAlert.loadinng(msg({ id: 'excelComingBack' }))
      setExporting(false)
      setComingBack(true)
    }
    if (comingBack && !dataFacade?.loading) {
      setComingBack(false)
      swalAlert.succesWithTimer(msg({ id: 'excelFinished' }))
    }
  }, [dataFacade?.loading])

  useEffect(() => {
    if (exporting) {
      swalAlert.loadinng(msg({ id: 'excelExporting' }))
    }
    dataFacade.reload()
  }, [exporting])

  const clearFilters = () => {
    setSelectedDateRange([undefined, undefined])
    setCurrentOwnstation(initialValues.ownStation)
    setCurrentSupplier(initialValues.supplier)
    setCurrentIsTotal(initialValues.isTotal)
  }

  const headerOptions = {
    title: (
      <Group w="100%">
        <AppRangePicker
          w="300px"
          disabled={dataFacade?.loading || exporting || comingBack}
          value={selectedDateRange}
          onChange={setSelectedDateRange}
        />
        <EntityDropdown
          w={180}
          entity="ownFuelStation"
          disabled={dataFacade?.loading}
          placeholder="ownStation"
          value={currentOwnStation}
          onChange={({ value }) => setCurrentOwnstation(value)}
        />
        <EntityDropdown
          w={180}
          entity="supplier"
          disabled={dataFacade?.loading}
          placeholder="supplier"
          value={currentSupplier}
          onChange={({ value }) => setCurrentSupplier(value)}
        />
        <EntityDropdown
          w={180}
          entity="total"
          options={[
            { value: 'true', label: t('total') },
            { value: 'false', label: t('partial') },
          ]}
          placeholder="total"
          value={currentIsTotal}
          onChange={({ value }) => setCurrentIsTotal(value)}
        />
        <AppButton
          w={180}
          preset="reset"
          disabled={dataFacade?.loading || exporting || comingBack}
          onClick={clearFilters}
        />
      </Group>
    ),
    handlerHeaderActions: {
      primaryButton: handleDownload,
      primaryButtonText: 'Excel',
      primaryButtonIcon: 'download',
    },
    searchOptions: dataFacade?.searchOptions,
  }

  const pagerOptions = {
    ...dataFacade?.pagerOptions,
    totalRecords: dataFacade?.data?.total,
    allowTotalRecords: true,
  }

  return (
    <AppSection
      header={<AppTabMenu options={purchaseFuels} breakpoint="xl" />}
      headerOptions={headerOptions}
    >
      <PurchaseSummaryDataTable
        loading={dataFacade?.loading}
        pagerOptions={pagerOptions}
        sortOptions={dataFacade?.sortOptions}
        value={dataFacade?.data?.resp}
      />
    </AppSection>
  )
}

export default Summary
